/**
 * Plain-language labels for self-assessment posture classification UI.
 * Internal classification keys and clinical values stay unchanged — only display copy.
 */

const PATTERN_LABELS: Record<string, string> = {
  swayback: 'Swayback',
  anterior: 'Anterior Pelvic Tilt (APT)',
  anteriorPlc: 'Anterior Pelvic Tilt (APT) moving to Swayback',
  stacked: 'Balanced posture',
};

const CHIP_LABELS: Record<string, string> = {
  swayback: 'Swayback',
  anterior: 'Anterior Pelvic Tilt',
  anteriorPlc: 'APT moving to Swayback',
  stacked: 'Balanced',
};

const PHASE1_GOAL_LABELS: Record<string, string> = {
  'Posterior Pelvic Tilt': 'Bring your body weight back toward your heels and get your lower back to let go by opening up the back side of your ribcage.',
  'Clear Hip Shift': 'Shift your body weight back and unclench the glued-shut back side of your hips so your joints can move freely again.',
  'Posterior Pelvic Tilt + Posterior Space Creation': 'Bring your center of gravity back and decompress the emergency brake on the back side of your hips.',
};

const KEY_METRIC_LABELS: Record<string, string> = {
  'Pelvic Tilt <5°': 'Less forward pelvic tilt (under 5°)',
  'Hip Extension 0°': 'Full hip extension',
  'SLR 50°': 'Straight leg raise to 50+ degrees',
  'Shoulder Flexion 170° (Chest)': 'Improved shoulder mobility (showing the chest is opening up)',
  'Shoulder Flexion 170° (Ribs)': 'Better shoulder mobility (showing the back of the ribs are opening)',
  'SLR 50° (PLC)': 'Straight leg raise to 50+ degrees (proving the back of the hips finally opened up)',
  'Breathing Without Neck Tension': 'Breathing without neck tension',
  'Cervical Rotation 30°': 'Improved neck rotation',
  'Shoulder Flexion 170° (FHP)': 'Better shoulder mobility (proving the ribcage is doing the breathing work, not the neck)',
};

const STRATEGY_LABELS: Record<string, string> = {
  'Horizontal Displacement of the hip relative to the ankle, asis/psis optional':
    'Side-view hip position compared to your ankle',
  'Clinical score rules (legacy default)': 'Combined posture measurements',
};

const LATERAL_BADGE_LABELS: Record<string, string> = {
  'Right (Standard)': 'Leaning slightly right',
  'Left (Reversed)': 'Leaning slightly left',
  Balanced: 'Well balanced left to right',
};

const REASONING_EXACT: Record<string, string> = {
  'Head Side-bent Left (Right Ear Higher)': 'Your head leans slightly to the left',
  'Head Side-bent Right (Left Ear Higher)': 'Your head leans slightly to the right',
  'Right Shoulder Lower': 'Your right shoulder sits a little lower',
  'Left Shoulder Lower': 'Your left shoulder sits a little lower',
  'Right Hip Lower': 'Your right hip sits a little lower',
  'Left Hip Lower': 'Your left hip sits a little lower',
  'Pelvic Shift Right': 'Your hips shift slightly to the right',
  'Pelvic Shift Left': 'Your hips shift slightly to the left',
  'Normal balanced lateral alignment': 'Your weight looks evenly balanced left to right',
  'Awaiting Anterior or Posterior view for lateralization analysis':
    'We need a front or back photo to check left–right balance',
};

const SIGNAL_EXACT: Record<string, string> = {
  'Ideally Stacked': 'Well aligned overall',
  'Torso Sway': 'Upper body sits behind your hips',
  'Knees Extended': 'Knees lock back slightly',
  'Torso Ant Lean': 'Upper body leans forward',
};

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[\s-_()]/g, '');
}

export function getLaymanPatternLabel(pattern: string | undefined): string {
  if (!pattern) return '';
  const key = normalizeKey(pattern);
  if (key.includes('sway')) return PATTERN_LABELS.swayback;
  if (key.includes('posteriorlowercompression') || key.includes('plc')) return PATTERN_LABELS.anteriorPlc;
  if (key.includes('anterior') || key.includes('apt')) return PATTERN_LABELS.anterior;
  if (key.includes('stack')) return PATTERN_LABELS.stacked;
  return pattern;
}

export function getLaymanPatternChipLabel(pattern: string): string {
  if (!pattern) return '';
  const key = normalizeKey(pattern);
  if (key.includes('sway')) return CHIP_LABELS.swayback;
  if (key.includes('posteriorlowercompression') || key.includes('plc')) return CHIP_LABELS.anteriorPlc;
  if (key.includes('anterior') || key.includes('apt')) return CHIP_LABELS.anterior;
  if (key.includes('stack')) return CHIP_LABELS.stacked;
  return pattern;
}

/**
 * Exact LeadConnectorHQ survey dropdown options for
 * "What was your posture classification?" (query keys
 * `posture_classification` and named alias `posture-classification`).
 * Prefill requires a 100% string match or the survey leaves the placeholder.
 */
export const CALENDLY_POSTURE_OPTIONS = [
  'Anterior Pelvic Tilt',
  'Swayback',
  'Anterior Pelvic Tilt Moving Towards Swayback',
  "I don't know",
] as const;

export type CalendlyPostureOption = (typeof CALENDLY_POSTURE_OPTIONS)[number];

/** Alias — same options power the LeadConnectorHQ booking survey. */
export const SURVEY_POSTURE_OPTIONS = CALENDLY_POSTURE_OPTIONS;
export type SurveyPostureOption = CalendlyPostureOption;

/**
 * Exact LeadConnectorHQ calendar form dropdown options on
 * posture-assessment-walkthrough-call (`single_dropdown_17fmt`).
 * Labels differ from the survey — prefill requires a 100% match.
 */
export const CALENDAR_POSTURE_OPTIONS = [
  'Anterior Pelvic Tilt',
  'Swayback',
  'Swayback Moving Into Anterior Pelvic Tilt',
  "I'm not sure",
] as const;

export type CalendarPostureOption = (typeof CALENDAR_POSTURE_OPTIONS)[number];

const SURVEY_TO_CALENDAR_POSTURE: Record<CalendlyPostureOption, CalendarPostureOption> = {
  'Anterior Pelvic Tilt': 'Anterior Pelvic Tilt',
  Swayback: 'Swayback',
  'Anterior Pelvic Tilt Moving Towards Swayback': 'Swayback Moving Into Anterior Pelvic Tilt',
  "I don't know": "I'm not sure",
};

/**
 * Map internal posture classification / display labels to the exact survey
 * dropdown option string. Check PLC / "moving towards" before plain swayback
 * so hybrid labels are not misclassified as Swayback.
 */
export function getCalendlyPostureOption(pattern: string | undefined): CalendlyPostureOption {
  if (!pattern?.trim()) return "I don't know";

  const trimmed = pattern.trim();
  const exact = CALENDLY_POSTURE_OPTIONS.find((opt) => opt === trimmed);
  if (exact) return exact;

  const key = normalizeKey(trimmed);

  // Hybrid APT→Swayback / PLC first (may also contain "sway")
  if (
    key.includes('moving') ||
    key.includes('towards') ||
    key.includes('toward') ||
    key.includes('posteriorlowercompression') ||
    key.includes('plc') ||
    key === 'apttoswayback'
  ) {
    return 'Anterior Pelvic Tilt Moving Towards Swayback';
  }

  if (key.includes('sway')) return 'Swayback';

  if (key.includes('anterior') || key.includes('apt') || key.includes('tilt')) {
    return 'Anterior Pelvic Tilt';
  }

  // Stacked / neutral / unknown — no matching survey option
  return "I don't know";
}

/** @deprecated Prefer getCalendlyPostureOption — kept as an alias for call sites. */
export const mapToCalendlyPostureOption = getCalendlyPostureOption;

/** Alias for survey/booking call sites. */
export const getSurveyPostureOption = getCalendlyPostureOption;

/**
 * Map internal / survey posture labels to the calendar dropdown option
 * strings. Hybrid and unknown labels differ from the survey.
 */
export function getCalendarPostureOption(pattern: string | undefined): CalendarPostureOption {
  if (!pattern?.trim()) return "I'm not sure";

  const trimmed = pattern.trim();
  const exact = CALENDAR_POSTURE_OPTIONS.find((opt) => opt === trimmed);
  if (exact) return exact;

  return SURVEY_TO_CALENDAR_POSTURE[getCalendlyPostureOption(trimmed)];
}

export function getLaymanPhase1Goal(goal: string | undefined): string | undefined {
  if (!goal) return goal;
  return PHASE1_GOAL_LABELS[goal] ?? goal;
}

export function getLaymanKeyMetric(metric: string): string {
  return KEY_METRIC_LABELS[metric] ?? metric;
}

export function getLaymanStrategyLabel(label: string | undefined): string {
  if (!label) return 'How we measured your posture';
  const trimmed = label.trim();
  return STRATEGY_LABELS[trimmed] ?? trimmed;
}

export function getLaymanLateralBadge(lateralized: string | undefined): string | undefined {
  if (!lateralized) return lateralized;
  return LATERAL_BADGE_LABELS[lateralized] ?? lateralized;
}

export function getLaymanGroundingSide(side: 'Left' | 'Right' | undefined): string | undefined {
  if (!side) return side;
  return side === 'Left' ? 'Left foot' : 'Right foot';
}

export function getLaymanReasoningLine(line: string | null | undefined): string {
  if (!line || typeof line !== 'string') return '';
  if (REASONING_EXACT[line]) return REASONING_EXACT[line];

  if (line.startsWith('Foot Presentation (Posterior):')) {
    return line
      .replace(/^Foot Presentation \(Posterior\):\s*/i, 'Foot angles (back view): ')
      .replace(/\bInv\b/g, 'rolls inward')
      .replace(/\bEv\b/g, 'rolls outward');
  }

  const strategyMatch = line.match(/^\[(.+?)\]\s+(.+?)\s+—\s+classification confidence\s+(\d+)%/);
  if (strategyMatch) {
    const [, , orientation] = strategyMatch;
    const friendlyOrientation = getLaymanPatternLabel(orientation);
    return `Indicators of ${friendlyOrientation.toLowerCase()} detected based on how your body lines up in your photos.`;
  }

  return line
    .replace(/\bAnterior Pelvic Tilt\b/gi, 'Anterior Pelvic Tilt (APT)')
    .replace(/\bPosterior Pelvic Tilt\b/gi, 'Posterior Pelvic Tilt')
    .replace(/\bSwayback\b/gi, 'Swayback')
    .replace(/\bStacked\b/gi, 'balanced posture')
    .replace(/\bLateralization\b/gi, 'left–right lean')
    .replace(/\bGrounding\b/gi, 'weight on one side');
}

export function getLaymanSignal(signal: string | null | undefined): string {
  if (!signal || typeof signal !== 'string') return '';
  if (SIGNAL_EXACT[signal]) return SIGNAL_EXACT[signal];

  if (/^Anterior pelvic tilt/i.test(signal)) return 'Pelvis tilts forward';
  if (/^Hip not far forward/i.test(signal)) return 'Hips stay over ankles';
  if (/^Hip anterior/i.test(signal)) return 'Hips sit forward of ankles';
  if (/^Pelvic Tilt \d/i.test(signal)) return 'Pelvis tilts forward';
  if (/^Hip Ant /i.test(signal)) return 'Hips sit forward of ankles';
  if (/^Knee flexion/i.test(signal)) return 'Knees bent slightly';
  if (/^Knee hyperextension/i.test(signal)) return 'Knees lock back slightly';
  if (/^Torso (anterior|posterior|sway)/i.test(signal)) return 'Upper body alignment shift';
  if (/^Head side-bent/i.test(signal)) return 'Head leans to one side';
  if (/^Forward head/i.test(signal)) return 'Head sits forward';
  if (/^Forward Head Posture/i.test(signal)) return 'Head sits forward';
  if (/ lateralization$/i.test(signal)) {
    return signal.includes('Left') ? 'Slight lean to the left' : 'Slight lean to the right';
  }
  if (/^Foot presentation$/i.test(signal)) return 'Foot angles differ left vs right';

  return signal;
}

export const LAYMAN_CLASSIFICATION_UI = {
  phase1GoalPrefix: 'First focus:',
  orientationConfidence: 'Overall pattern',
  lateralizationConfidence: 'Left–right balance',
  groundingConfidence: 'Weight-side',
  keyMetricsHeading: 'First targets to work on',
  footPresentationHeading: 'Foot angle (back view)',
  evidenceHeading: 'Why we think this',
  evidenceSubheading: 'What we measured',
  globalEvidenceTitle: 'What we noticed overall',
  lateralEvidenceTitle: 'Left–right balance clues',
  evidenceTip:
    'These estimates come from your photos. Confidence goes up when more views are available and measurements are clear.',
  signalsHeading: 'Quick summary tags',
  signalsSubheading: 'Short list for your report',
  nextStepsHeading: 'What to do next',
  nextStepsSubheading: 'Simple next steps',
  nextStepConfirmTitle: 'Start with the exercises below',
  nextStepConfirmBody:
    'Pick 2–3 exercises and do them consistently. Small, regular practice matters more than intensity.',
  nextStepPhaseTitle: 'Track how you feel',
  nextStepPhaseBody:
    'Notice whether standing feels easier and your lower back feels less arched after a few weeks of practice.',
} as const;
